import { useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { Studio } from '../studio/Studio';
import { Onboarding } from '../onboarding/Onboarding';
import {
  createProject,
  ensureActiveProject,
  listProjectSummaries,
  openProject,
  useProject,
  useProjectLibrary,
} from '../projectStore';
import { Landing } from '../landing/Landing';
import { ProjectsHome } from '../projects/ProjectsHome';

const studioSearchSchema = z.object({
  view: z.enum(['model', 'visual']).catch('model'),
  concept: z.enum(['good', 'better', 'best']).catch('better'),
  start: z.enum(['plan', 'projects']).optional(),
});

export const Route = createFileRoute('/')({
  validateSearch: studioSearchSchema,
  component: RenovationExperience,
});

function RenovationExperience() {
  const completed = useProject((state) => state.completed);
  const activeId = useProject((state) => state.activeId);
  const summaries = useProjectLibrary((state) => listProjectSummaries(state));
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const goHome = () => void navigate({ search: (previous) => ({ view: previous.view ?? 'model', concept: previous.concept ?? 'better' }) });
  const goProjects = () => void navigate({ search: (previous) => ({ view: previous.view ?? 'model', concept: previous.concept ?? 'better', start: 'projects' }) });
  const goPlan = () => void navigate({ search: (previous) => ({ view: previous.view ?? 'model', concept: previous.concept ?? 'better', start: 'plan' }) });

  useEffect(() => {
    if (search.start === 'plan' && !activeId) ensureActiveProject();
  }, [search.start, activeId]);

  if (search.start === 'projects') {
    return (
      <ProjectsHome
        onOpen={() => goPlan()}
        onCreate={() => {
          createProject();
          goPlan();
        }}
      />
    );
  }

  if (search.start === 'plan') {
    if (!activeId) return null;
    return completed ? <Studio /> : <Onboarding />;
  }

  const latest = summaries[0];

  return (
    <Landing
      projectCount={summaries.length}
      latestProjectName={latest?.name}
      onOpenProjects={summaries.length ? goProjects : undefined}
      onStart={() => {
        createProject();
        goPlan();
      }}
      onContinue={latest ? () => {
        openProject(latest.id);
        goPlan();
      } : undefined}
      onBrand={goHome}
    />
  );
}
